// SEQ-180 E17b: Profit vs Overtime and Profit vs Patching scatters. Both
// project points onto the same Y axis (profit %); only the X metric and dot
// colour differ, so one parameterised chart backs both exported components.
//
// SEQ-182 T13: the overtime scatter's X axis defaults to Overtime Hours %
// (labour hours worked as overtime, normalised so a $20m project is not
// automatically read as "worse" than a $2m one just for being bigger, plan
// section 6.5 / ticket "Profit vs Overtime Hours"), with a toggle for the
// raw Overtime Hours figure and the original Overtime Cost % Revenue view.
// Axis label and unit always come from the selected option's own `unit`, so
// the toggle can never leave a percent sign on an hours axis.
//
// SEQ-180 legibility follow-up (15 September 2026). THE OUTLIERS OWNED THE
// AXIS. Measured on production data: a Y domain of -1500% to 500%, because
// Tapestry - Chevron Island sits at -1360.1% profit, with La Belle - Palm Beach
// and ORIA - Spring Hill at -268.7% and -254.7%. Twenty of twenty-two points
// then shared a band a few pixels tall, so Echo - Tugun at 48% and Aire -
// Newstead at 2.7% were the same picture. Profit vs patching did it sideways,
// 0% to 600%, off the same three projects.
//
// Both axes now clamp (chartDomain.ts, which explains why a clamp and not a log
// scale), and both charts say so twice: a point that ran off is drawn as a
// triangle on the edge it ran off rather than as an ordinary dot, and a note
// under the chart names every one of them with its real figure and opens it on
// click. The clamp exists so twenty projects can be compared. The note exists
// so the three that were cut are never the ones the page quietly stopped
// mentioning.
import { useMemo, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, ReferenceLine, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { Target } from "lucide-react";
import type { ProjectRollup } from "../../features/pnlAnalytics/rollup";
import { pnlChartCardStyle, formatPnlMetric } from "../../features/pnlAnalytics/pnlAnalyticsFormat";
import { formatMetricValue } from "../../lib/pnl/formatMetricValue";
import { clampedAxisDomain, outsideDomain, pinToDomain, type AxisDomain } from "../../features/pnlAnalytics/chartDomain";
import { countPlottable, isPlottableRow } from "../../features/pnlAnalytics/chartEmptiness";
import { chartAnimation } from "../../features/pnlAnalytics/pnlMotion";
import { PnlAxisNote, PnlChartEmpty, PnlChartHead, type PinnedProject } from "./PnlChartFrame";
import { PnlProjectTooltip, type ProjectTooltipRow } from "./PnlProjectTooltip";
import "./PnlWidgetCharts.css";

type ScatterProject = ProjectRollup & { projectName: string };

type ScatterAxisUnit = "percent" | "hours";

function axisUnitSuffix(unit: ScatterAxisUnit): string {
  return unit === "percent" ? "%" : " hrs";
}

interface ScatterXOption {
  key: "overtimePctRevenue" | "patchingPctRevenue" | "overtimeHoursPct" | "overtimeHours";
  label: string;
  unit: ScatterAxisUnit;
  /** The metric in two or three words, for a sentence: "501.1% patching". */
  shortName: string;
  /** One plain sentence: what this X axis measures, for a reader who has not seen it before. */
  meaning: string;
}

/**
 * A plotted point: the project's own fields, plus the coordinates it is DRAWN
 * at and whether either of them is a clamped edge.
 *
 * FLAT, NOT NESTED. Recharts hands a tooltip the datum it was given, so a
 * `{ project, plotX }` wrapper would make the tooltip reach through a field it
 * has to know about, and the first version of this did exactly that and
 * rendered "Unnamed project" with a column of NaN. The project's real figures
 * stay under their own names; only `plotX` and `plotY` are ever allowed to be
 * an edge, and nothing reads those except the axes.
 */
type ScatterPoint = ScatterProject & {
  plotX: number;
  plotY: number;
  pinnedX: boolean;
  pinnedY: boolean;
};

/** The context rows every scatter tooltip carries under its two axes. */
const SCATTER_CONTEXT_ROWS: ProjectTooltipRow[] = [
  { name: "Revenue", key: "revenue", unit: "currency" },
  { name: "Cost", key: "cost", unit: "currency" },
  { name: "Profit", key: "profit", unit: "currency" },
  { name: "Overtime", key: "overtime", unit: "currency" },
  { name: "Overtime hours", key: "overtimeHours", unit: "hours" },
  { name: "Patching", key: "patching", unit: "currency" },
  { name: "Labour cost / hr", key: "labourCostPerHour", unit: "currency_per_hour" },
  { name: "Profit / hr", key: "profitPerLabourHour", unit: "currency_per_hour" },
];

const CHART_HEIGHT = 320;

/**
 * The mark a pinned point is drawn as: a triangle pointing at the edge the
 * project ran off.
 *
 * A different SHAPE rather than only a different colour, because the point is
 * not at its real coordinates and must not be readable as one that is. A
 * triangle also carries a direction, so the mark itself says which way the
 * project went, and a reader who never reaches the note under the chart still
 * knows something ran off this side.
 *
 * Drawn by hand rather than through Recharts' built-in "triangle" symbol,
 * because that symbol takes its size from the shared ZAxis and came out at
 * about six pixels in a corner, which is dust rather than a mark for the most
 * important project on the page.
 */
const PINNED_MARK_HALF_WIDTH = 8;

interface PinnedMarkProps {
  cx?: number;
  cy?: number;
  payload?: ScatterPoint;
  xDomain: AxisDomain;
  yDomain: AxisDomain;
}

function PinnedMark({ cx = 0, cy = 0, payload, xDomain, yDomain }: PinnedMarkProps) {
  const h = PINNED_MARK_HALF_WIDTH;
  let points = `${cx},${cy + h} ${cx - h},${cy - h} ${cx + h},${cy - h}`;
  if (payload?.pinnedY) {
    const below = (payload.profitPercent ?? 0) < yDomain[0];
    points = below
      ? `${cx},${cy + h} ${cx - h},${cy - h} ${cx + h},${cy - h}`
      : `${cx},${cy - h} ${cx - h},${cy + h} ${cx + h},${cy + h}`;
  } else if (payload?.pinnedX) {
    const past = (payload.plotX ?? 0) >= xDomain[1];
    points = past
      ? `${cx + h},${cy} ${cx - h},${cy - h} ${cx - h},${cy + h}`
      : `${cx - h},${cy} ${cx + h},${cy - h} ${cx + h},${cy + h}`;
  }
  return <polygon points={points} fill="var(--color-error-text)" stroke="var(--bg-secondary)" strokeWidth={1.5} />;
}

interface PnlScatterChartProps {
  title: string;
  /** What the pair of axes is FOR, in one sentence, under the title. */
  caption: string;
  /** One option: no toggle rendered. More than one: a segmented toggle above the chart, defaulting to xOptions[0]. */
  xOptions: ScatterXOption[];
  dotColor: string;
  projects: ScatterProject[];
  /** SEQ-240: the "How is this worked out" trigger beside the title. */
  info?: ReactNode;
}

function PnlScatterChart({ title, caption, xOptions, dotColor, projects, info }: PnlScatterChartProps) {
  const navigate = useNavigate();
  const reduce = useReducedMotion() ?? false;
  const [selectedIdx, setSelectedIdx] = useState(0);
  const xOption = xOptions[selectedIdx] ?? xOptions[0];

  // One predicate for "this project can be drawn", shared with the empty state
  // below and with the other three charts (chartEmptiness.ts). A scatter that
  // filters on one rule and empties on another is how two of these charts ended
  // up drawing an axis with nothing on it.
  const plotted = useMemo(
    () => projects.filter((p) => isPlottableRow(p, ["profitPercent", xOption.key])),
    [projects, xOption.key],
  );

  const xAxis = useMemo(() => clampedAxisDomain(plotted.map((p) => p[xOption.key])), [plotted, xOption.key]);
  const yAxis = useMemo(() => clampedAxisDomain(plotted.map((p) => p.profitPercent)), [plotted]);

  const points = useMemo<ScatterPoint[]>(
    () =>
      plotted.map((project) => {
        const x = project[xOption.key] as number;
        const y = project.profitPercent as number;
        return {
          ...project,
          plotX: pinToDomain(x, xAxis.domain),
          plotY: pinToDomain(y, yAxis.domain),
          pinnedX: outsideDomain(x, xAxis.domain) !== null,
          pinnedY: outsideDomain(y, yAxis.domain) !== null,
        };
      }),
    [plotted, xOption.key, xAxis.domain, yAxis.domain],
  );

  const inRange = useMemo(() => points.filter((p) => !p.pinnedX && !p.pinnedY), [points]);
  const pinned = useMemo(() => points.filter((p) => p.pinnedX || p.pinnedY), [points]);

  // Worst first: a reader with one question about this chart is asking which
  // project is off the end of it.
  //
  // The reading names EVERY axis the project ran past, not just one. Tapestry -
  // Chevron Island is off both ends of the patching chart, and reporting only
  // its profit would leave the 501.1% patching that caused it unsaid on the one
  // chart that exists to show it.
  const pinnedProjects = useMemo<PinnedProject[]>(
    () =>
      [...pinned]
        .sort((a, b) => (a.profitPercent ?? 0) - (b.profitPercent ?? 0))
        .map((point) => {
          const readings: string[] = [];
          if (point.pinnedY) readings.push(`${formatPnlMetric(point.profitPercent, "percent")} profit`);
          if (point.pinnedX) {
            const value = formatMetricValue(point[xOption.key], xOption.unit === "hours" ? "hours" : "percent");
            readings.push(`${value} ${xOption.shortName}`);
          }
          return { projectId: point.projectId, projectName: point.projectName, reading: readings.join(" and ") };
        }),
    [pinned, xOption.key, xOption.unit, xOption.shortName],
  );

  const openProject = (projectId: string) => navigate(`/projects/${projectId}?tab=status`);

  // Recharts' Scatter onClick payload shape has varied across versions
  // (sometimes the raw data point, sometimes a symbol-props object wrapping
  // it in `.payload`); reading both covers either.
  //
  // Mouse-only by design: recharts scatter dots have no keyboard focus
  // target of their own. The project performance table's project name link
  // (PnlProjectPerformanceTable.tsx) is the keyboard-accessible path to the
  // same destination for every project plotted here, so this is not a
  // keyboard-access gap to fix on the chart itself (round 2 code review).
  const handleDotClick = (clicked: unknown) => {
    const candidate = clicked as { payload?: ScatterPoint } & Partial<ScatterPoint>;
    const point = candidate?.payload ?? (candidate as ScatterPoint | undefined);
    if (point?.projectId) openProject(point.projectId);
  };

  // `pinnedBy` names the field on the POINT that says whether this particular
  // project ran off this particular axis. Marking the row from the chart-level
  // `clamped` flag instead told every point on a clamped chart that it was
  // pinned, including the twenty that were drawn exactly where they belong.
  const tooltipRows: ProjectTooltipRow[] = [
    { name: "Profit %", key: "profitPercent", unit: "percent", axis: true, colour: dotColor, pinnedBy: "pinnedY" },
    {
      name: xOption.label,
      key: xOption.key,
      unit: xOption.unit === "hours" ? "hours" : "percent",
      axis: true,
      colour: dotColor,
      pinnedBy: "pinnedX",
    },
    ...SCATTER_CONTEXT_ROWS,
  ];

  const beyond = yAxis.clamped
    ? xAxis.clamped
      ? "both axes of this chart"
      : "the profit axis"
    : `the ${xOption.label.toLowerCase()} axis`;

  return (
    <div style={pnlChartCardStyle} className="pnl-chart-card">
      <PnlChartHead
        icon={<Target size={18} aria-hidden="true" style={{ color: dotColor }} />}
        title={title}
        info={info}
        caption={`${caption} ${xOption.meaning} Select a point to open that project.`}
      >
        {xOptions.length > 1 && (
          // SEQ-182: same shape as .pnl-year-tabs / .pnl-year-tab
          // (src/index.css, the range bar's own segmented toggle), reused
          // here as .pnl-segmented-toggle / .pnl-segmented-toggle-btn.
          //
          // The active segment used to take its background from this chart's
          // dotColor through an inline style, which put white 12.5px text on
          // amber at 2.15:1. The active state now comes from the stylesheet and
          // is the product's own, measuring 4.94:1. A chart colour is a mark; it
          // is not a background for text.
          <div role="group" aria-label={`${title}: choose the X axis metric`} className="pnl-segmented-toggle">
            {xOptions.map((opt, idx) => (
              <button
                key={opt.key}
                type="button"
                className="pnl-segmented-toggle-btn"
                onClick={() => setSelectedIdx(idx)}
                aria-pressed={idx === selectedIdx}
              >
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </PnlChartHead>

      {countPlottable(projects, ["profitPercent", xOption.key]) === 0 ? (
        <PnlChartEmpty height={CHART_HEIGHT} />
      ) : (
        <motion.div
          className="pnl-chart-body"
          style={{ width: "100%", height: CHART_HEIGHT }}
          // Switching the X metric re-scales both axes, so every point means
          // something new. Keyed on the choice, the chart cross-fades to the new
          // reading instead of sliding twenty dots to positions on a different
          // ruler.
          key={xOption.key}
          initial={reduce ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: reduce ? 0 : 0.2 }}
        >
          <ResponsiveContainer>
            <ScatterChart margin={{ top: 10, right: 24, left: 4, bottom: 24 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
              <XAxis
                type="number"
                dataKey="plotX"
                name={xOption.label}
                unit={axisUnitSuffix(xOption.unit)}
                domain={xAxis.domain}
                allowDataOverflow
                tick={{ fontSize: 12, fill: "var(--text-secondary)" }}
                label={{ value: xOption.label, position: "insideBottom", offset: -14, fontSize: 12, fill: "var(--text-secondary)" }}
              />
              <YAxis
                type="number"
                dataKey="plotY"
                name="Profit %"
                unit="%"
                domain={yAxis.domain}
                allowDataOverflow
                tick={{ fontSize: 12, fill: "var(--text-secondary)" }}
                label={{ value: "Profit %", angle: -90, position: "insideLeft", fontSize: 12, fill: "var(--text-secondary)" }}
              />
              <ZAxis range={[80, 80]} />
              {/* Break-even, which every point on this chart is read against and
                  which the old auto domain buried under the outliers. */}
              <ReferenceLine y={0} stroke="var(--text-secondary)" strokeWidth={1} />
              <Tooltip content={(<PnlProjectTooltip rows={tooltipRows} />) as never} cursor={{ strokeDasharray: "3 3" }} />
              <Scatter data={inRange} cursor="pointer" onClick={handleDotClick} {...chartAnimation(reduce, 0)}>
                {inRange.map((point) => (
                  <Cell key={point.projectId} fill={dotColor} />
                ))}
              </Scatter>
              {/* A different SHAPE, not merely a different colour: a pinned
                  point is not at its real coordinates, and it must not be
                  readable as one that is. */}
              <Scatter
                data={pinned}
                shape={(<PinnedMark xDomain={xAxis.domain} yDomain={yAxis.domain} />) as never}
                cursor="pointer"
                onClick={handleDotClick}
                {...chartAnimation(reduce, 1)}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </motion.div>
      )}

      <PnlAxisNote beyond={beyond} projects={pinnedProjects} onOpenProject={openProject} />
    </div>
  );
}

// SEQ-182 AC08: the normalised comparison is what ships as the default,
// because a $20m project naturally works more overtime HOURS than a $2m
// one; the raw-hours and original cost-based views stay one click away.
const OVERTIME_X_OPTIONS: ScatterXOption[] = [
  {
    key: "overtimeHoursPct",
    shortName: "overtime hours",
    label: "Overtime hours %",
    unit: "percent",
    meaning: "Across is the share of labour hours worked as overtime, so a big job is not read as worse just for being big.",
  },
  {
    key: "overtimeHours",
    shortName: "overtime hours",
    label: "Overtime hours",
    unit: "hours",
    meaning: "Across is overtime hours as worked, unadjusted for the size of the job.",
  },
  {
    key: "overtimePctRevenue",
    shortName: "overtime cost",
    label: "Overtime cost % revenue",
    unit: "percent",
    meaning: "Across is what overtime cost, as a share of what the job brought in.",
  },
];

export function PnlProfitVsOvertimeScatter({ projects, info }: { projects: ScatterProject[]; info?: ReactNode }) {
  return (
    <PnlScatterChart
      info={info}
      title="Profit vs overtime"
      caption="One point per project. Up the side is profit as a share of that project's own revenue."
      xOptions={OVERTIME_X_OPTIONS}
      dotColor="var(--color-warning)"
      projects={projects}
    />
  );
}

const PATCHING_X_OPTIONS: ScatterXOption[] = [
  {
    key: "patchingPctRevenue",
    shortName: "patching",
    label: "Patching % revenue",
    unit: "percent",
    meaning: "Across is what patching cost, as a share of what the job brought in.",
  },
];

export function PnlProfitVsPatchingScatter({ projects, info }: { projects: ScatterProject[]; info?: ReactNode }) {
  return (
    <PnlScatterChart
      info={info}
      title="Profit vs patching"
      caption="One point per project. Up the side is profit as a share of that project's own revenue."
      xOptions={PATCHING_X_OPTIONS}
      // Was #8b5cf6, a literal violet with no token behind it and no other use
      // in the product. The brand blue is already this page's accent and is the
      // only colour here a reader has been taught to associate with anything.
      dotColor="var(--seq-blue)"
      projects={projects}
    />
  );
}
